import { useEffect, useRef, useState, memo, useContext, useMemo, Fragment } from 'react'
import Input from '../input/Input'
import Select from '../select/Select'
import PlanSelect from '../select/PlanSelect'
import SelectUnits from '../select/SelectUnits'
import PlanSelectPopup from '../planSelectPopup/PlanSelectPopup'
import { minimizedAgeOptions } from '../../service/inputUtil'
import { appContext } from '../../service/AppContext'
import DatePicker from '../datePicker/DatePicker'
import { simulatorContext } from '../../service/SimulatorContext'
import { kidAgeMonths, kidSelectedRoutes, rowMaxUnits, getAvailablePlans, kidTotalUnits, TOTAL_UNITS_PER_CHILD } from '../../service/planUtils'
import SummaryItemDetailsRow from '../summary/SummaryItemDetailsRow'
import Checkbox from '../input/Checkbox'

const SimulatorChildCard = memo(({ index = 0, id, name, age, plans = [], marriage, personId, personIdValidation, updateCB, removeKid, addPlanRow, removePlanRow, preBorn, birthday, removeDisabled = false }) => {

  const { urlAgent, plans: allPlans } = useContext(appContext)
  const { completedKids } = useContext(simulatorContext)

  // Flattened entries of this kid (one per plan row) - aggregate for stock market details
  const currentKidEntries = useMemo(() => completedKids.filter(kid => kid.kidId === id), [completedKids, id]);
  const stockMarketSum = useMemo(() => {
    const entries = currentKidEntries.filter(kid => kid.stockMarket);
    if (!entries.length) return null;
    return entries.reduce((acc, kid) => {
      Object.keys(kid.stockMarket).forEach(key => { acc[key] = (acc[key] || 0) + kid.stockMarket[key] });
      return acc
    }, {})
  }, [currentKidEntries]);

  const [minimize, setMinimze] = useState(false)
  const [popupRowId, setPopupRowId] = useState(null) //Popup state - the plan row being edited
  const cardRef = useRef(true)
  const inputRef = useRef()

  const kid = { id, name, age, plans, marriage, preBorn, birthday };
  const firstRow = plans[0];
  const ageMonths = kidAgeMonths(kid);
  const availableCount = getAvailablePlans(allPlans, ageMonths).length;
  const canAddPlan = kidTotalUnits(kid) < TOTAL_UNITS_PER_CHILD && getAvailablePlans(allPlans, ageMonths, kidSelectedRoutes(kid)).length > 0;

  const planTitleWithPrefix = (plan) => (plan && age >= 1 && age <= 3) ? 'מרכזית - ' + plan?.title : plan?.title;

  const handleMinimize = (e) => {
    setMinimze(prev => !prev)
  }

  useEffect(() => {
    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
    const timeoutId = setTimeout(() => {
      inputRef.current?.focus();
    }, 400);

    return () => {
      if (timeoutId)
        clearTimeout(timeoutId)
    }
  }, [])

  // Open / close the popup based on the update signal (multiple available plans => auto open)
  const handlePlanSignal = (signal) => {
    if (signal?.openPopup)
      setPopupRowId(signal.openPopupRowId)
    else
      setPopupRowId(null)
  }

  const handleUpdateAge = (e) => {
    if (typeof (updateCB) === 'function')
      handlePlanSignal(updateCB(id, 'age')(e));
  }

  const handleUpdateBirthday = (e) => {
    if (typeof (updateCB) === 'function')
      handlePlanSignal(updateCB(id, 'birthday')(e));
  }

  const handleUpdatePreBorn = (value) => {
    if (typeof (updateCB) === 'function')
      handlePlanSignal(updateCB(id, 'preBorn')(value))
  }

  const handleAddPlanRow = () => {
    if (typeof (addPlanRow) === 'function') {
      const rowId = addPlanRow(id);
      setPopupRowId(rowId);
    }
  }

  const handleKeyDown = (e) => {
    if (typeof (handleMinimize) !== 'function') return
    if ([13, 32].includes(e.keyCode)) {
      e.preventDefault();
      handleMinimize(e)
    }
  }

  return (
    <div ref={cardRef} className="child-card" >
      <div className="title-wrapper">
        <div>
          <span className="child-index">{index + 1}.</span>
          {(name) ? <span className="child-title">{name}</span> : <span className="child-title gray-title">שם הילד</span>}
        </div>
        <img className={`card-minimize ${(minimize) ? 'minimized' : ''}`} src="./icons/chevron-up.svg" alt='הקטנת כרטיס' onClick={handleMinimize} tabIndex={0} onKeyDown={handleKeyDown} />
      </div>
      <hr className="card-hr-desktop" />

      {/* <!-- Start Cards  --> */}
      <div className="card-content-wrapper">

        <div className="card-inner">
          <div className="fields-flex">
            <Input myRef={inputRef} label="שם הילד/ה" className="child-name" name="child-name" placeholder="שם פרטי של הילד/ה" value={name} onChange={updateCB(id, 'name')} disabled={preBorn} />
            <Input label="מספר תעודת זהות (אופציונלי)" className="child-person-id" name="child-person-id" value={(preBorn) ? '' : personId} onChange={updateCB(id, 'personId')} maxlength="9" invalid={personIdValidation?.invalid} errorText={personIdValidation?.errorText} disabled={preBorn} />
          </div>
          <div className="fields-flex">
            <Select className="child-age" name="child-age" label="גיל" value={age} options={minimizedAgeOptions} onChange={handleUpdateAge} />
            {(urlAgent.enabledBirthDay) &&
              <div className='birthday-wrapper'>
                <DatePicker type={(preBorn) ? 'text' : 'date'} label="תאריך לידה" className="child-birthday" name="child-birthday" value={(preBorn) ? 'הילוד עדיין לא נולד' : birthday} onChange={handleUpdateBirthday} disabled={preBorn} max={new Date().toJSON().slice(0, 10)} >
                  <Checkbox type='checkbox' label="טרום לידה -הילוד עדיין לא נולד" className="child-preBorn" name="child-preBorn" checked={preBorn} onChange={handleUpdatePreBorn} />
                </DatePicker>
              </div>
            }
            <PlanSelect className={`child-plan ${(preBorn || !!name) ? '' : 'desktop-or-wide'}`} label={(plans.length > 1) ? 'מסלול 1' : 'מסלול'} tooltip="לכל גיל מסלול שונה. אנא בחרו את המסלול המתאים לכם." value={planTitleWithPrefix(firstRow?.plan)}
              setOpenPopup={() => setPopupRowId(firstRow?.rowId)} disabled={(age == null || availableCount === 0)} required={true} errorText={(availableCount === 0 && age != null) ? "אין מסלולים לגיל זה" : "יש לבחור מסלול"} OnValueButtonLabel={(age > 3) && 'פרטים'} />
            <Select className={`child-marriage ${(preBorn || !!name) ? '' : 'desktop-or-wide'}`} name="child-marriage" label="גיל נישואים צפוי" tooltip=".כדי שנוכל להתאים לכם תוכנית מדויקת לחתונה של הילד/ה, יש להעריך את גיל הנישואין העתידי" value={marriage} onChange={updateCB(id, 'marriage')}
              options={[18, 19, 20, 21, 22, 23, 24]} />
          </div>
        </div>
      </div>
      <hr className={`card-hr-mobile`} style={(marriage) ? {} : { display: 'none' }} />
      <div className={`card-sum ${(marriage) ? '' : 'desktop-or-wide'}`}  >
        {/* Primary plan units - original position (top-row plan select drives this) */}
        <SelectUnits value={firstRow?.units} onChange={updateCB(id, 'rowUnits', firstRow?.rowId)} max={rowMaxUnits(kid, firstRow?.rowId, firstRow?.plan)} />
      </div>
      {/* Additional plan rows (multi-plan) - direct card-grid children so they mirror the first plan:
          plan select lands in the right column, units in the left column */}
      {plans.slice(1).map((row, i) => {
        const rowIndex = i + 1;
        return (
          <Fragment key={row.rowId}>
            <div className='plan-row-plan'>
              <div className="card-hr-mobile" />
              <button className="remove-item remove-plan-row align-center" onClick={() => removePlanRow(id, row.rowId)}><img src="./icons/x-mark.svg" alt='הסר' />הסרת מסלול</button>
              <PlanSelect className='child-plan' label={`מסלול ${rowIndex + 1}`} tooltip="לכל גיל מסלול שונה. אנא בחרו את המסלול המתאים לכם." value={planTitleWithPrefix(row.plan)}
                setOpenPopup={() => setPopupRowId(row.rowId)} disabled={(age == null || availableCount === 0)} required={true} errorText={(availableCount === 0 && age != null) ? "אין מסלולים לגיל זה" : "יש לבחור מסלול"} OnValueButtonLabel={(age > 3) && 'פרטים'} />
            </div>
            <div className='plan-row-units'>
              <SelectUnits value={row.units} onChange={updateCB(id, 'rowUnits', row.rowId)} max={rowMaxUnits(kid, row.rowId, row.plan)} />
            </div>
          </Fragment>
        )
      })}
      {(canAddPlan) &&
        <button className="add-plan-row" onClick={handleAddPlanRow}><img src="./icons/plus-circle-blue.svg" alt='+' />הוספת מסלול</button>}
      {(!!stockMarketSum) && <div className="child-card-stock-market">
        <SummaryItemDetailsRow label='סה"כ רווח עד החתונה' value={stockMarketSum?.totalPrfit} />
        <SummaryItemDetailsRow label='מיסוי צפוי על הרווח (25%)' value={stockMarketSum?.profitVat} />
        <SummaryItemDetailsRow label='סה"כ לחתונה (הפקדה + רווח)' value={stockMarketSum?.totalForWedding} />
        <SummaryItemDetailsRow label='סה"כ השלמה מהלוואות בנקאיות' value={stockMarketSum?.completeWithLoan} />
        <SummaryItemDetailsRow label='תשלום ריבית' value={stockMarketSum?.loanInterest} />
        <SummaryItemDetailsRow label='החזר חודשי בהלוואה בנקאית' value={stockMarketSum?.loanMonthlyPayment} />
        <SummaryItemDetailsRow label='סה"כ הוצאות במסלול שוק ההון' value={stockMarketSum?.totalPayment} tooltip="הפקדה בשוק ההון + תשלום עבור הלוואה בנקאית" />
        <SummaryItemDetailsRow label='סה"כ הוצאות בגמ"ח' value={stockMarketSum?.totalGLBpayment} tooltip="תרומה + החזר הלוואה פחות המענק" />
        <SummaryItemDetailsRow label='חיסכון במסלול הגמ"ח' value={stockMarketSum?.totalDiff} />
      </div>}
      {(popupRowId) && <PlanSelectPopup closePopup={() => setPopupRowId(null)} setPlan={updateCB(id, 'rowPlan', popupRowId)} name={name} age={age} preBorn={preBorn} setAge={handleUpdateAge}
        ageMonths={ageMonths} excludeRoutes={kidSelectedRoutes(kid, popupRowId)} />}
      <button className="remove-item align-center" onClick={(e) => { removeKid(id) }} disabled={removeDisabled}><img src="./icons/x-mark.svg" alt='הסר' />הסרה מרשימה</button>
    </div>
  )
})
export default SimulatorChildCard
